import { blade } from '../kit';
import { C, type Hex, mix, type Pix } from '../pix';

/**
 * Shared craft for the hand-made item and goods pictures in this folder: hue-shifted ramps
 * (shadows lean cool plum, lights lean warm cream) and the few shapes several icons reuse.
 * The composer still lights the outer edge from the upper right and outlines the picture; these
 * add the inner form (ridge lines, shadow halves, bands) the edge pass cannot.
 */

const COOL = '#2a1a4a';
const WARM = '#fff2c8';

/** One material in five steps: two cool shadows, the base, two warm lights. */
export interface Ramp {
  readonly d2: Hex;
  readonly d1: Hex;
  readonly b: Hex;
  readonly l1: Hex;
  readonly l2: Hex;
}

export const ramp = (b: Hex): Ramp => ({
  d2: mix(b, COOL, 0.62),
  d1: mix(b, COOL, 0.32),
  b,
  l1: mix(b, WARM, 0.38),
  l2: mix(b, WARM, 0.72),
});

/** Materials used across the set. */
export const R = {
  iron: ramp('#7c8698'),
  steel: ramp('#b8c4d4'),
  bronze: ramp('#c08a3a'),
  gold: ramp('#e8b43a'),
  wood: ramp('#8a5a36'),
  lacquer: ramp('#a8282a'),
  red: ramp('#d8342e'),
  jade: ramp('#3fae7c'),
  paper: ramp('#e8dcc0'),
  ink: ramp('#3a3448'),
  violet: ramp('#8a5ad0'),
  azure: ramp('#4a8ae0'),
} as const;

const unit = (x0: number, y0: number, x1: number, y1: number) => {
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  return { dx: (x1 - x0) / len, dy: (y1 - y0) / len, len };
};

/**
 * A blade with form: the shadow half, the lit half, a ridge line along the middle and a bright
 * cutting edge. `curve` bends it like `kit.blade`.
 */
export function formedBlade(
  p: Pix,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  r: Ramp,
  o: { w?: number; curve?: number; taper?: number; ridge?: boolean } = {},
): void {
  const w = o.w ?? 3;
  const curve = o.curve ?? 0;
  const taper = o.taper ?? 0.8;
  blade(p, x0, y0, x1, y1, { w, curve, col: r.d1, taper, edge: r.l2 });
  const { dx, dy } = unit(x0, y0, x1, y1);
  const s = w * 0.25;
  blade(p, x0 - dy * s, y0 + dx * s, x1, y1, { w: w * 0.5, curve, col: r.b, taper, edge: r.l1 });
  if (o.ridge !== false && w >= 3) {
    const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) * taper * 0.9);
    for (let i = 1; i < n; i++) {
      const t = i / n;
      const bend = curve * Math.sin(t * Math.PI * 0.85);
      p.px(x0 + (x1 - x0) * t - dy * bend, y0 + (y1 - y0) * t + dx * bend, r.l1);
    }
  }
}

/** A cord-wrapped grip: alternating dark and light diamonds along a 2-px handle. */
export function wrappedGrip(p: Pix, x0: number, y0: number, x1: number, y1: number, cord: Ramp): void {
  p.thick(x0, y0, x1, y1, cord.d1, 2);
  const n = Math.round(Math.hypot(x1 - x0, y1 - y0));
  for (let i = 0; i <= n; i += 2) {
    const t = i / Math.max(1, n);
    p.px(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, cord.l1);
  }
}

/** A long shaft (staff, spear, glaive) with a lit line along its upper side. */
export function shaft(p: Pix, x0: number, y0: number, x1: number, y1: number, r: Ramp, w = 2): void {
  p.thick(x0, y0, x1, y1, r.d1, w);
  p.line(x0, y0 - 1, x1, y1 - 1, r.b);
  if (w >= 3) p.line(x0 + 1, y0 - 1, x1 + 1, y1 - 1, r.l1);
}

/** A small tassel hanging from (x,y): a gold knot and red strands, `len` long. */
export function tassel(p: Pix, x: number, y: number, len = 6, col: Ramp = R.red): void {
  p.rect(x - 1, y, 2, 2, R.gold.b);
  p.px(x, y, R.gold.l2);
  for (let k = -1; k <= 1; k++) p.line(x + k, y + 2, x + k * 2, y + 2 + len, k ? col.d1 : col.b);
  p.px(x, y + 3, col.l1);
}

/** A bead or gem with a glint, lit from the upper right. */
export function gem(p: Pix, cx: number, cy: number, rad: number, r: Ramp): void {
  p.ell(cx, cy, rad, rad, r.d1);
  p.ell(cx + rad * 0.2, cy - rad * 0.2, rad * 0.7, rad * 0.7, r.b);
  p.ell(cx + rad * 0.35, cy - rad * 0.35, rad * 0.32, rad * 0.32, r.l1);
  p.px(cx + rad * 0.4, cy - rad * 0.5, C.white);
}

/**
 * A dao (single-edged sabre) as a drawn shape, not a feather: a straight back, a broad belly that
 * widens toward the clipped point. Guard at (gx,gy), point toward (tx,ty); the edge faces down-right.
 */
export function daoBlade(
  p: Pix,
  gx: number,
  gy: number,
  tx: number,
  ty: number,
  w: number,
  r: Ramp,
  o: { belly?: number; groove?: boolean } = {},
): void {
  const { dx, dy, len: L } = unit(gx, gy, tx, ty);
  const nx = -dy;
  const ny = dx;
  const at = (u: number, v: number): [number, number] => [gx + dx * u + nx * v, gy + dy * u + ny * v];
  const w1 = w * (1 + (o.belly ?? 0.4));
  const back: [number, number][] = [at(0, -1), at(L * 0.86, -1), at(L, -1.8)];
  const edge: [number, number][] = [
    at(L * 0.94, w1 * 0.4),
    at(L * 0.84, w1 * 0.85),
    at(L * 0.7, w1),
    at(L * 0.35, (w + w1) * 0.48),
    at(0, w),
  ];
  p.poly([...back, ...edge], r.d1);
  p.poly(
    [at(1, w * 0.35), at(L * 0.8, w * 0.35), at(L * 0.9, w1 * 0.45), at(L * 0.82, w1 - 1), at(L * 0.68, w1 - 1), at(L * 0.35, (w + w1) * 0.48 - 1), at(1, w - 1)],
    r.b,
  );
  p.path([at(L, -1.8), ...edge.slice(0, 4)], r.l2);
  p.path([at(0, -1), at(L * 0.86, -1)], r.d2);
  if (o.groove !== false) p.path([at(L * 0.06, w * 0.12), at(L * 0.62, w * 0.12)], r.l1);
}
